import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import { getMe } from '@/api/services/auth.service'
import { useAuthStore } from '@/stores/useAuthStore'
import { AppLoader } from '@/components/common/AppLoader'
import { isPublicPath } from './publicPaths'

interface AuthInitProps {
  children: React.ReactNode
}


/**
 * Runs a one-time session check on mount (GET /users/me with credentials).
 * If the backend returns 200, sets user and isAuthenticated from the response.
 * Renders children after the check completes so PrivateRoute sees correct auth state.
 */
export function AuthInit({ children }: AuthInitProps): React.ReactElement {
  const [sessionCheckDone, setSessionCheckDone] = useState(false)
  // Marketing pages render without waiting for the session check (it still
  // runs in the background). Decided once: later navigation keeps the tree mounted.
  const [skipGate] = useState(() => isPublicPath())
  const [unreachable, setUnreachable] = useState(false)
  const setAuth = useAuthStore((s) => s.setAuth)

  useEffect(() => {
    let cancelled = false
    getMe({ isSessionCheck: true })
      .then((user) => {
        if (!cancelled) setAuth(user)
      })
      .catch((error: unknown) => {
        // A 401 simply means "not signed in" — leave the store unauthenticated.
        // No response at all means the API is unreachable; surface that instead
        // of leaving the user on a spinner that never resolves.
        if (!cancelled && isAxiosError(error) && !error.response) {
          setUnreachable(true)
        }
      })
      .finally(() => {
        if (!cancelled) setSessionCheckDone(true)
      })
    return () => {
      cancelled = true
    }
  }, [setAuth])

  if (!sessionCheckDone && !skipGate) {
    return (
      <AppLoader />
    )
  }

  // A marketing page doesn't need the API, so an unreachable one doesn't block it.
  if (unreachable && !skipGate) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Can&apos;t reach the server
        </h1>
        <p className="max-w-md text-sm text-gray-600 dark:text-gray-400">
          The API isn&apos;t responding. Check that the backend is running, then try again.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          Retry
        </button>
      </div>
    )
  }

  return <>{children}</>
}
